import {
  PayPalScriptProvider,
  PayPalButtons,
  PayPalButtonsComponentProps,
} from '@paypal/react-paypal-js';
import {
  selectUserProfileId,
  selectCartInfo,
  selectPayPalInfo,
  selectDynSess,
} from '@next-app/lib/features/InitSelectors';
import { useSelector, useDispatch } from 'react-redux';
import { PDPState } from '@next-app/lib/features/PDP/PDPSlice.interface';
import { makeFetchCall } from '@next-app/utils/fetch-data';
import { usePathname, useRouter } from 'next/navigation';
import classes from './PayPalButton.module.scss';
import paypalLogo from '/public/assets/images/paypal-logo.svg';
import GenericImage from '@next-app/components/elements/GenericImage/GenericImage';
import { Suspense, useEffect, useState } from 'react';
import Conditional from '@next-app/components/Conditional';
import dynamic from 'next/dynamic';
import { showDynamicModal } from '@next-app/utils/common-utils';
import { ShippingAddressModalData } from '@next-app/constants/constants';
import { DynamicDTMEvent } from '@next-app/utils/dtm-event';
import { selectPaymentMethodType } from '@next-app/lib/features/checkout/CheckoutSelector';
import { PAYMENT } from '@next-app/constants/CheckoutConstants';
import {
  setIsSubmitOrder,
  setReloadLoadCheckoutDetails,
} from '@next-app/lib/features/checkout/CheckoutSlice';

type PayPalButtonColor = 'gold' | 'blue' | 'silver' | 'white' | 'black';

const ShippingAddressModal = dynamic(
  () =>
    import(
      '@next-app/components/shared/ShippingAddressModal/ShippingAddressModal'
    ),
  { ssr: true },
);

interface PayPalButtonProps {
  openRegistryModal: Function | undefined;
  showRegistryAddressModal: boolean;
  showClearTheListAddressModal?: boolean;
  isCheckout?: boolean;
  showCustomlogo?: boolean;
  height?: number;
  buttonColor?: PayPalButtonColor;
  customClass?: string;
  handleSuccessPayment?: Function;
  isFurniture?: boolean;
  useRegistryAddress?: boolean;
  cartId?: string;
}

export default function PayPalButton({
  showRegistryAddressModal,
  openRegistryModal,
  showClearTheListAddressModal,
  isCheckout = false,
  showCustomlogo,
  height,
  buttonColor,
  customClass,
  handleSuccessPayment,
  isFurniture,
  useRegistryAddress,
  cartId,
}: PayPalButtonProps) {
  const profileId = useSelector((state: PDPState) =>
    selectUserProfileId(state),
  );
  const cartInfo = useSelector((state: PDPState) => selectCartInfo(state));
  const payPalInfo = useSelector((state: PDPState) => selectPayPalInfo(state));
  const selectedPaymentMethodType = useSelector(selectPaymentMethodType);
  const session = useSelector((state: PDPState) => selectDynSess(state));

  const [clientId, setClientId] = useState<string | null>(null);

  const [showRegistryModal, setShowRegistryModal] = useState(false);
  const [showClearListModal, setShowClearListModal] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<{
    data: any;
    actions: any;
  } | null>(null);

  useEffect(() => {
    setClientId(payPalInfo?.['client-id']);
  }, [payPalInfo]);

  const dispatch = useDispatch();

  const initialOptions = {
    clientId: payPalInfo?.['client-id'],
    'disable-funding': payPalInfo?.['disable-funding'],
    intent: payPalInfo?.intent,
  };
  const router = useRouter();
  const pathname = usePathname();

  const styles: PayPalButtonsComponentProps['style'] = {
    disableMaxWidth: true,
    label:
      selectedPaymentMethodType === PAYMENT.PAYPAL_OPTION ? 'pay' : 'paypal',
    layout: 'vertical',
    height: height,
    color: buttonColor ? buttonColor : 'white',
    shape: isFurniture ? 'sharp' : 'rect',
  };

  const createOrder = async () => {
    try {
      let checkoutBody: {
        _dynSessConf: string;
        isPayPalFromCheckout: boolean;
        useRegistryAddress?: boolean;
      } = {
        _dynSessConf: session,
        isPayPalFromCheckout: true,
      };
      if (useRegistryAddress) {
        checkoutBody = { ...checkoutBody, useRegistryAddress };
      }

      const addToCartBody = {
        profileId: profileId,
        cartId: cartInfo?.cartId || cartId,
        _dynSessConf: session,
        isPayPalFromCheckout: false,
      };

      const requestPayload = {
        endpoint:
          '/rest/model/atg/commerce/order/purchase/PurchaseActor/applyPayPalLight',
        options: {
          method: 'POST',
        },
        headers: {
          'Content-Type': 'application/json',
          'Is-Furniture': isFurniture ? 'true' : 'false',
        },
        body: isCheckout ? checkoutBody : addToCartBody,
      };

      const response = await makeFetchCall(requestPayload);

      if (response.formError) {
        console.log('There are errors inside the form', response);
      }
      return response?.payPalToken;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const onApprove = async (data: any) => {
    try {
      const requestPayload = {
        endpoint:
          '/rest/model/atg/commerce/order/purchase/PurchaseActor/doExpressCheckout',
        options: {
          method: 'POST',
        },
        headers: {
          'Content-Type': 'application/json',
          'Is-Furniture': isFurniture ? 'true' : 'false',
        },
        body: {
          profileId: profileId,
          cartId: cartInfo?.cartId || cartId,
          token: data.orderID,
          PayerId: data.payerID,
          _dynSessConf: session,
        },
      };
      const response = await makeFetchCall(requestPayload);
      if (response.status === 'OK') {
        if (isCheckout) {
          dispatch(setIsSubmitOrder(true));
          if (typeof handleSuccessPayment === 'function')
            handleSuccessPayment();
        } else {
          router.push('/checkout-page');
        }
      }
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const onClick = async (data: any, actions: any) => {
    DynamicDTMEvent({ event: 'payPalCheckoutInitiatedEvent' });
    if (
      (showRegistryAddressModal || showClearTheListAddressModal) &&
      openRegistryModal
    ) {
      setPendingPayment({ data, actions });
      setShowRegistryModal(showRegistryAddressModal);
      setShowClearListModal(showClearTheListAddressModal ?? false);

      showDynamicModal();
      return actions.reject();
    }
    return actions.resolve();
  };

  const onInit = async (data: any, actions: any) => {
    // Disable the button if showRegistryAddressModal`` is true
    if (showRegistryAddressModal) {
      return actions.disable();
    } else {
      actions.enable();
    }
  };

  const onCancel = async () => {
    if (isCheckout) {
      window.location.reload();
    } else {
      router.push(pathname);
    }
  };

  function handleModalClick() {
    setShowRegistryModal(false);
    setShowClearListModal(false);

    if (pendingPayment) {
      pendingPayment.actions.resolve();

      setPendingPayment(null);
    }
  }

  if (!clientId) return <p>Loading PayPal...</p>;

  return (
    <>
      <PayPalScriptProvider options={initialOptions}>
        <div
          className={`${customClass} position-relative overflow-hidden ${classes['paypal-button-wrp']} ${isFurniture ? classes['f-style'] : ''}`}
        >
          <PayPalButtons
            style={styles}
            createOrder={createOrder}
            onApprove={onApprove}
            onClick={onClick}
            onInit={onInit}
            onCancel={onCancel}
          />
          <Conditional
            if={showCustomlogo !== undefined && showCustomlogo !== false}
          >
            <div className={`text-center ${classes['paypal-logo-button']}`}>
              <GenericImage
                src={paypalLogo}
                alt="PayPal Logo"
                width={80}
                height={18}
              />
            </div>
          </Conditional>
        </div>
      </PayPalScriptProvider>

      <Conditional if={showRegistryModal || showClearListModal}>
        <Suspense fallback={'Loading...'}>
          <ShippingAddressModal
            showModal={showRegistryModal || showClearListModal}
            setShowModal={setShowRegistryModal}
            handleModalClick={handleModalClick}
            isCheckoutPage={isCheckout}
            isRegistryModal={showRegistryModal}
            modalTitle={
              showRegistryModal
                ? ShippingAddressModalData.registryTitle
                : ShippingAddressModalData.clearListTitle
            }
            modalBodyContent={
              showRegistryModal
                ? ShippingAddressModalData.registryContent
                : ShippingAddressModalData.clearListContent
            }
          />
        </Suspense>
      </Conditional>
    </>
  );
}
